import { useEffect, useState } from 'react';
import { type Translator } from '../translations/translation-types';

declare global {
	interface Window {
		grecaptcha: {
			execute: (siteKey: string, action: unknown) => Promise<string>;
			ready: (fn: () => void) => void;
		};
	}
}

const hideBadge = () => {
	if (!window.grecaptcha) return;
	window.grecaptcha.ready(() => {
		const badge = document.getElementsByClassName('grecaptcha-badge')[0] as HTMLElement;
		if (!badge) return;
		badge.style.visibility = 'hidden';
	});
};

const usePreAuthScripts = (
	isPreAuthEnabled: boolean,
	loadI4go: boolean,
	loadWallets: boolean,
	t: Translator
): {
	i4goScriptLoaded: boolean;
	reCaptchaLoaded: boolean;
	generateReCaptchaToken: (action: string) => Promise<string>;
} => {
	const [reCaptchaLoaded, setReCaptchaLoaded] = useState<boolean>(false);
	const [i4goScriptLoaded, setI4goScriptLoaded] = useState<boolean>(false);

	useEffect(() => {
		if (i4goScriptLoaded || !loadI4go || !isPreAuthEnabled) return;

		const resources = loadWallets
			? [
				`${process.env.I4GO_HOST}/css/wallets.css`,
				`https://pay.google.com/gp/p/js/pay.js`,
				`${process.env.I4GO_HOST}/js/wallets.js`,
				`${process.env.I4GO_HOST}/js/jquery.i4goTrueToken.js`
			]
			: [`${process.env.I4GO_HOST}/js/jquery.i4goTrueToken.js`];

		const scriptCount = resources.filter(r => !r.endsWith('.css')).length;
		let loadedCount = 0;

		resources.forEach(src => {
			if (src.endsWith('.css')) {
				const link = document.createElement('link');
				link.rel = 'stylesheet';
				link.href = src;
				document.head.appendChild(link);
				return;
			}

			const script = document.createElement('script');
			script.async = true;
			script.src = src;

			script.onload = () => {
				loadedCount++;
				if (loadedCount === scriptCount) {
					setI4goScriptLoaded(true);
				}
			};

			script.onerror = () => {
				setI4goScriptLoaded(false);
			};

			document.body.appendChild(script);
		});
	}, [isPreAuthEnabled, i4goScriptLoaded, loadI4go, loadWallets]);

	useEffect(() => {
		localStorage.removeItem('_grecaptcha');
		if (typeof window === 'undefined' || reCaptchaLoaded || !isPreAuthEnabled) return;
		if (window.grecaptcha) {
			hideBadge();
			setReCaptchaLoaded(true);
			return;
		}
		const script = document.createElement('script');
		script.async = true;
		script.src = `https://www.recaptcha.net/recaptcha/api.js?render=${process.env.REACT_APP_RECAPTCHA_SITE_KEY}`;
		script.onerror = () => {
			setReCaptchaLoaded(false);
		};
		script.addEventListener('load', () => {
			setReCaptchaLoaded(true);
			hideBadge();
		});
		document.body.appendChild(script);
		return hideBadge;
	}, [isPreAuthEnabled, reCaptchaLoaded]);

	const generateReCaptchaToken = (action: string): Promise<string> => {
		return new Promise((resolve, reject) => {
			const errorMessage = t('hooks:usePreAuthScripts:error:recaptchaNotLoaded:message');
			if (!reCaptchaLoaded) return reject(new Error(errorMessage));
			if (typeof window === 'undefined' || !window.grecaptcha) {
				setReCaptchaLoaded(false);
				return reject(new Error(errorMessage));
			}
			window.grecaptcha.ready(() => {
				window.grecaptcha
					.execute(process.env.REACT_APP_RECAPTCHA_SITE_KEY || '', { action })
					.then((token: string) => {
						resolve(token);
					});
			});
		});
	};

	return { i4goScriptLoaded, reCaptchaLoaded, generateReCaptchaToken };
};

export default usePreAuthScripts;
